import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info } from 'lucide-react';

interface TooltipProps {
  content: string;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  width?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  title,
  children,
  className = '',
  position = 'top',
  width = '300px'
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const getPositionStyles = () => {
    switch (position) {
      case 'bottom':
        return 'top-full left-1/2 -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 -translate-y-1/2 ml-2';
      default: // top
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
    }
  };

  const getArrowStyles = () => {
    switch (position) {
      case 'bottom':
        return '-top-2 left-1/2 -translate-x-1/2 rotate-180';
      case 'left':
        return 'top-1/2 -right-2 -translate-y-1/2 rotate-90';
      case 'right':
        return 'top-1/2 -left-2 -translate-y-1/2 -rotate-90';
      default: // top
        return '-bottom-2 left-1/2 -translate-x-1/2';
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className="inline-flex items-center gap-1 cursor-help"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children || (
          <div className="p-1 rounded-full hover:bg-emerald-500/10 transition-colors">
            <Info className="w-4 h-4 text-emerald-500" />
          </div>
        )}
      </div>
      
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className={`absolute z-50 p-4 text-sm bg-slate-800/95 backdrop-blur-sm text-slate-200 rounded-lg shadow-lg border border-emerald-500/20 ${getPositionStyles()}`}
            style={{ width }}
          >
            <div className={`absolute w-4 h-4 rotate-45 bg-slate-800/95 border-t border-l border-emerald-500/20 ${getArrowStyles()}`} />
            <div className="relative">
              {title && (
                <h4 className="font-medium text-emerald-500 mb-2">{title}</h4>
              )}
              <div className="text-slate-300 space-y-2">
                {content.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};